import { mdiMicrosoftExcel } from '@mdi/js'
import Icon from '@mdi/react'
import DeleteIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import ExpandIcon from '@mui/icons-material/Fullscreen'
import RotateIcon from '@mui/icons-material/RotateRight'
import ErrorIcon from '@mui/icons-material/WarningRounded'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import { FileLoader, FileValidator } from 'components/FileHelpers'
import GifPlayer from 'components/GifPlayer'
import { NativeVideoPlayer } from 'components/VideoPlayer'
import { useObjectUrl } from 'hooks/useObjectUrl'
import { FileType } from 'interfaces'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { LINKEDIN_DOCUMENT_EXTENSIONS } from 'shared'
import { ALLOWED_BULK_EXTENSIONS, getFileTypeByMimeType } from 'utils/file'
import styles from './FileUpload.pcss'
import { Upload } from './Upload'

const PROGRESS_COMPLETE = 100
const PROGRESS_CONTENT_MAX = 90

export type UploadHandle = { abort: (terminate?: boolean) => void }

interface FileUploadBaseProps {
  file: File
  className?: string
  validation?: {
    maxSize?: number // bytes
    allowedFormats?: string[]
  }
  selected?: boolean
  rotateBy?: string
  mediaElementId?: string
  withProgress?: boolean
  skipValidation?: boolean
  loaderClassName?: string
  abortOnUnmount?: boolean
  fileUrl?: string
  hideExpandButton?: boolean
  expandOnContentClick?: boolean
  onRotate?: (fileName: string) => void
  onImageLoaded?: (name: string, src: string) => void
  onExpand?: (url: string, type: 'image' | 'video') => void
}

// Every callback echoes back the `uploadId` the caller passed in, so the id type is a parameter:
// `FileUploadProps<string>` makes `uploadId` required and hands handlers a plain `string`, while the
// default `FileUploadProps` keeps it optional for callers that render a single unidentified file.
interface FileUploadIdProps<Id extends string | undefined> {
  onClick?: (fileName: string, uploadId: Id) => void
  onDelete?: (fileName: string, uploadId: Id) => void
  onEdit?: (uploadId: Id) => void
  onError?: (fileName: string, error: string, uploadId: Id) => void
  onFileUploaded: (url: string, file: File, uploadId: Id) => void
  onUploadStarted?: (upload: UploadHandle, uploadId: Id) => void
}

export type FileUploadProps<Id extends string | undefined = string | undefined> =
  FileUploadBaseProps
  & FileUploadIdProps<Id>
  & (undefined extends Id ? { uploadId?: Id } : { uploadId: Id })

const FileUploadComponent = function FileUpload(props: FileUploadProps) {
  // NOTE: default via destructuring — React 19 no longer applies `defaultProps` to function components.
  const { withProgress = true } = props
  const intl = useIntl()
  const [validationDone, setValidationDone] = React.useState(false)
  const [isValid, setIsValid] = React.useState(false)
  const [error, setError] = React.useState<string | undefined>(undefined)
  const [fileSrc, setFileSrc] = React.useState<string | undefined>(undefined)
  const [fileUrl, setFileUrl] = React.useState<string | null>(null)
  const [progress, setProgress] = React.useState(0)
  const fileType = getFileTypeByMimeType(props.file.type)
  const videoObjectUrl = useObjectUrl(fileType === FileType.Video ? props.file : undefined)
  const fileExtension = props.file.name?.substring(props.file.name.lastIndexOf('.')) || ''
  const isExcelFile = ALLOWED_BULK_EXTENSIONS.includes(fileExtension)
  const isDocumentFile = LINKEDIN_DOCUMENT_EXTENSIONS.includes(fileExtension)
  const rotateImageStyles = props.rotateBy !== undefined && fileType === FileType.Image
    ? { transform: `rotate(${props.rotateBy}deg)` }
    : undefined
  const uploadRef = React.useRef<any>(undefined)

  // EXPL: <Upload> recreates its subject (aborting the running upload) whenever the result handlers
  // change identity, so `onUploadError`/`onFileUploaded` must stay stable. They read the current
  // props through this ref instead of closing over the first render — `file` is swapped in place when
  // an image comes back from the photo editor, and the old one must not be reported to the parent.
  const latest = React.useRef({ props, intl })
  React.useEffect(() => {
    latest.current = { props, intl }
  })

  React.useEffect(() => {
    return () => {
      if (props.abortOnUnmount && uploadRef.current) {
        uploadRef.current.abort(true)
      }
    }
  }, [props.abortOnUnmount])

  // EXPL: the same instance is reused when `file` is swapped (photo editor export), so everything the
  // previous file left behind has to go — its upload url is deleted from the server, its data url
  // would keep the old image on screen, and its upload handle would make `abortOnUnmount` abort the
  // upload that replaced it. This resets during render rather than in an effect on purpose:
  // <FileValidator> is a child, so it re-validates the new file BEFORE any effect here runs and a
  // reset scheduled there would wipe the error it just reported, leaving a tile with no preview and
  // no message.
  const previousFile = React.useRef(props.file)
  if (previousFile.current !== props.file) {
    previousFile.current = props.file
    uploadRef.current = undefined
    setFileUrl(null)
    setProgress(0)
    setError(undefined)
    setFileSrc(undefined)
    setIsValid(false)
    setValidationDone(false)
  }

  const onError = (fileName: string, message: string) => {
    if (props.onError) {
      props.onError(fileName, message, props.uploadId)
    }
  }

  // EXPL: derived rather than held in `error` state — setting state for it during render reported the
  // same file to the parent twice, once before and once after the reset above.
  const invalidTypeError = fileType === undefined && !isExcelFile && !isDocumentFile
    ? intl.formatMessage({ id: 'file.messages.invalid-type' })
    : undefined

  React.useEffect(() => {
    if (!invalidTypeError) {
      return
    }
    const { props: current } = latest.current
    if (current.onError) {
      current.onError(current.file.name, invalidTypeError, current.uploadId)
    }
  }, [invalidTypeError, props.file])

  const onValidationPassed = () => {
    setIsValid(true)
    setValidationDone(true)
  }

  const onValidationFailed = (error: string) => {
    setIsValid(false)
    setValidationDone(true)
    setError(error)
    onError(props.file.name, error)
  }

  const onFileLoadSuccess = (src: string) => {
    setFileSrc(src)
    if (props.onImageLoaded) {
      props.onImageLoaded(props.file.name, src)
    }
  }

  const onFileLoadFailed = (error: string) => {
    setError(error)
    onError(props.file.name, error)
    setFileSrc(undefined)
  }

  const handleClick = () => {
    if (props.onClick) {
      props.onClick(props.file.name, props.uploadId)
    }
  }

  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (props.onDelete) {
      props.onDelete(props.file.name, props.uploadId)
    }
  }

  const onEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (props.onEdit) {
      props.onEdit(props.uploadId)
    }
  }

  const toggleRotate = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (props.onRotate) {
      props.onRotate(props.file.name)
    }
  }

  const onUploadError = React.useCallback((_error: Error) => {
    const { props: current, intl: currentIntl } = latest.current
    const message = currentIntl.formatMessage({ id: 'uploads.error.upload-failed' })
    if (current.onError) {
      current.onError(current.file.name, message, current.uploadId)
    }
    setError(message)
    // EXPL: must keep a stable identity — it's passed to <Upload>, whose effect tears down and
    // recreates the upload subject whenever onError/onSuccess change, aborting the in-flight upload.
    // Current values come from the `latest` ref.
  }, [])

  const onFileUploaded = React.useCallback((url: string) => {
    const { props: current } = latest.current
    setProgress(PROGRESS_COMPLETE)
    setFileUrl(url)
    current.onFileUploaded(url, current.file, current.uploadId)
    // EXPL: stable identity required — see onUploadError above; changing it would restart <Upload>.
  }, [])

  const onUploadProgress = React.useCallback((value: number) => {
    // NOTE: Set max of 95 upload progress in the onProgress event.
    // 100 is set when the request is complete
    const visibleProgress = Math.min(value, PROGRESS_CONTENT_MAX)
    setProgress(visibleProgress)
  }, [])

  // EXPL: <Upload> only invokes the handlers its effect closed over, and that effect deliberately does
  // not re-run on every render — so these have to read the current props through `latest`, exactly
  // like onUploadError/onFileUploaded above.
  const onUploadStarted = React.useCallback((upload: UploadHandle) => {
    const { props: current } = latest.current
    uploadRef.current = upload
    if (current.onUploadStarted) {
      current.onUploadStarted(upload, current.uploadId)
    }
  }, [])

  const onExpand = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    const url = props.fileUrl || fileUrl
    const type = fileType === FileType.Video ? 'video' : 'image'
    if (props.onExpand) {
      props.onExpand(url as string, type)
    }
  }

  const canExpand = Boolean(props.onExpand && (props.fileUrl || fileUrl))

  const onContentClick = (e: React.MouseEvent) => {
    if (props.expandOnContentClick && canExpand) {
      onExpand(e)
    }
  }

  const displayError = error || invalidTypeError
  const errorClassName = displayError ? styles['with-error'] : ''
  const selectedClassName = props.selected ? styles.selected : ''
  const isStaticImage = fileSrc && fileType === FileType.Image
  const isVideo = fileType === FileType.Video
  const imageUrl = isStaticImage ? props.fileUrl || fileSrc : fileSrc
  const withExpandBtn = props.onExpand && props.fileUrl && !props.hideExpandButton

  return (
    <div
      className={`${styles.wrapper} ${props.className || ''} ${errorClassName} ${selectedClassName}`}
      data-testid="file-upload"
      onClick={handleClick}
    >
      <FileValidator
        file={props.file}
        maxSize={props.validation?.maxSize}
        allowedFileFormats={props.validation?.allowedFormats}
        disabled={props.skipValidation}
        onValidationError={onValidationFailed}
        onValidationSuccess={onValidationPassed}
      >
        <div
          className={`${styles.content} ${props.expandOnContentClick && canExpand ? styles['content-expandable'] : ''}`}
          onClick={onContentClick}
        >
          {validationDone && isValid && fileType !== FileType.Video && (
            <FileLoader
              file={props.file}
              withProgress
              className={`${styles.loader} ${props.loaderClassName || ''}`}
              onError={onFileLoadFailed}
              onLoaded={onFileLoadSuccess}
            />
          )}
          <div className={styles.actions} data-testid="file-upload-actions">
            {props.onDelete && (
              <IconButton size="small" className={styles['btn-delete']} onClick={onDelete}>
                <DeleteIcon className={styles.icon} />
              </IconButton>
            )}
            {props.onEdit && (isStaticImage || (isVideo && fileUrl)) && (
              <IconButton size="small" className={styles['btn-edit']} onClick={onEdit}>
                <EditIcon className={styles.icon} />
              </IconButton>
            )}
            {isStaticImage && props.onRotate && (
              <IconButton size="small" className={styles['btn-rotate']} onClick={toggleRotate}>
                <RotateIcon className={styles.icon} />
              </IconButton>
            )}
            {withExpandBtn && (
              <IconButton size="small" className={styles['btn-expand']} onClick={onExpand}>
                <ExpandIcon className={styles.icon} />
              </IconButton>
            )}
          </div>
          {isStaticImage && (
            <img
              src={imageUrl}
              style={rotateImageStyles}
              className={styles.image}
              data-id={props.mediaElementId}
              data-testid={props.mediaElementId}
            />
          )}
          {fileSrc && fileType === FileType.Gif && (
            <GifPlayer gifDataUrl={fileSrc} gifUrl="" mediaElementId={props.mediaElementId} />
          )}
          {validationDone && isValid && videoObjectUrl && (
            <NativeVideoPlayer
              videoDataUrl={videoObjectUrl}
              togglePlayOnHover
              muted
              key={props.file.name}
              className={styles.video}
              playBtnClassName={styles['btn-play']}
              mediaElementId={props.mediaElementId}
            />
          )}
          {isExcelFile && (
            <Icon path={mdiMicrosoftExcel} size="42" />
          )}
          {displayError && (
            <div className={styles['error-msg']}>
              <span className={`${styles.filename} text-ellipsis`} title={props.file.name}>
                <ErrorIcon className={styles['err-icon']} />
                {props.file.name}
              </span>
              <p>{displayError}</p>
            </div>
          )}
          {validationDone && isValid && !props.fileUrl && (
            <Upload
              file={props.file}
              onError={onUploadError}
              onProgress={onUploadProgress}
              onSuccess={onFileUploaded}
              onUploadStarted={onUploadStarted}
            />
          )}
        </div>
      </FileValidator>
      {withProgress && (
        <LinearProgress
          variant="determinate"
          value={progress}
          classes={{ bar: styles.bar }}
          className={styles.loader}
        />
      )}
    </div>
  )
}

// The implementation is written against the widest id type; the generic signature is what callers see.
// Sound because the component only ever passes its own `uploadId` prop back to the handlers.
export const FileUpload = React.memo(FileUploadComponent) as
  <Id extends string | undefined = string | undefined>(props: FileUploadProps<Id>) => React.JSX.Element

export default FileUpload
